import { DecimalPipe, NgOptimizedImage } from '@angular/common';
import { Component, inject, signal, computed, OnInit, OnDestroy, effect } from '@angular/core';
import { SPACE_HERO_IMAGE_SIZES } from '../../shared/storage/space-image.constants';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { take } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { environment } from '../../../environments/environment';
import { SpacePublicRouteService } from '../../services/space-public-route.service';
import { BookingService, CheckoutAmountQuote } from '../../services/booking.service';
import { FeaturesAgentService } from '../../services/features-agent.service';
import {
  DEFAULT_CONDITIONS, DEFAULT_PRICING, formatEuro, minHourlyRate, minHoursForSpace, quoteMinBookingTotal, Space, spaceCoverThumb,
} from '../../models/asset.model';
import { SeoService } from '../../services/seo.service';
import { buildSpaceSeoDescription, buildSpaceSeoTitle } from '../../shared/seo/seo.utils';
import { buildSpaceJsonLd } from '../../shared/seo/space-json-ld.utils';
import { GroupedFeatures } from '../../models/feature.model';
import { SpaceCalendarModalComponent } from '../space-calendar-modal/space-calendar-modal.component';
import { SpaceReviewsSectionComponent } from '../space-reviews-section/space-reviews-section.component';

@Component({
  selector: 'app-space-detail',
  standalone: true,
  imports: [RouterLink, NgOptimizedImage, DecimalPipe, LucideAngularModule, SpaceCalendarModalComponent, SpaceReviewsSectionComponent],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './space-detail.component.html',
})
export class SpaceDetailComponent implements OnInit, OnDestroy {
  readonly heroImageSizes = SPACE_HERO_IMAGE_SIZES;

  private route          = inject(ActivatedRoute);
  private router         = inject(Router);
  private auth           = inject(Auth);
  private sanitizer      = inject(DomSanitizer);
  private spaceRoutes    = inject(SpacePublicRouteService);
  private bookingService = inject(BookingService);
  private featuresAgent  = inject(FeaturesAgentService);
  private seo            = inject(SeoService);

  readonly space    = signal<Space | null>(null);
  readonly loading  = signal(true);
  readonly activeImageIndex = signal(0);
  readonly shareModalOpen    = signal(false);
  readonly calendarModalOpen = signal(false);
  readonly linkCopied        = signal(false);

  /** Native share sheet (Android / iOS / some desktop browsers). */
  readonly canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  /** Flips to true once /categories and /features have been fetched. */
  readonly featuresLoaded = signal(false);
  /** null while resolving auth state; non-null when signed in. */
  readonly authUser = toSignal(authState(this.auth), { initialValue: null });

  /** Venue record (alias for product copy). */
  readonly venue = computed(() => this.space());

  /** No slot picker on the public page — stays 0 until the booking flow. */
  readonly selectedHours = signal(0);

  readonly minHours = computed(() => {
    const v = this.venue();
    return v ? minHoursForSpace(v) : DEFAULT_PRICING.minHours;
  });

  readonly deposit = computed(() => this.venue()?.securityDeposit ?? 0);

  /** Floor total aligned with "Desde X€/h" × min hours (rental only, no cleaning). */
  readonly totalPrice = computed(() => {
    const v = this.venue();
    return v ? quoteMinBookingTotal(v) : 0;
  });

  /** CTA stays enabled here; duration is chosen on `/reservar`. */
  readonly isMinHoursMet = computed(() => {
    const h = this.selectedHours();
    return h === 0 || h >= this.minHours();
  });

  /** Lowest configured €/h for the sticky price block. */
  readonly fromHourlyRate = computed(() => {
    const s = this.space();
    return s ? minHourlyRate(s) : 0;
  });

  /** Gallery as full+thumb pairs: strip uses thumb (light), viewport uses full. */
  readonly gallery = computed<{ full: string; thumb: string }[]>(() => {
    const s = this.space();
    if (s?.images?.length) {
      return s.images.map(i => ({ full: i.urlFull, thumb: i.urlThumb || i.urlFull }));
    }
    return (s?.imageUrls ?? []).map(u => ({ full: u, thumb: u }));
  });

  readonly galleryImages = computed<string[]>(() => this.gallery().map(g => g.full));

  readonly conditions = computed(() => ({
    ...DEFAULT_CONDITIONS,
    ...(this.venue()?.conditions ?? {}),
  }));

  readonly cleaningFee = computed(() => this.venue()?.pricing?.cleaningFee ?? DEFAULT_PRICING.cleaningFee);

  readonly cancellationSummary = computed(() =>
    `Cancelacion gratuita hasta ${this.conditions().cancellationDays} dias antes`,
  );

  readonly cancellationLateHint =
    'Si se cancela tarde, se perdera el importe de la senal de reserva.';

  readonly depositSummary = computed(() =>
    `${this.formatPrice(this.deposit())} (Reembolsable)`,
  );

  readonly depositHint =
    'Se abona previo al evento y se devuelve en un plazo maximo de 24h tras verificar el local.';

  readonly cleaningSummary = 'Limpieza basica obligatoria (Gratuita)';

  readonly cleaningHint = computed(() =>
    `El espacio se debe dejar barrido y recogido, o contratar el servicio extra por ${this.formatPrice(this.cleaningFee())}.`,
  );

  readonly ageRequirementSummary = computed(() =>
    `Edad minima: ${this.conditions().minAge} anos`,
  );

  readonly checkoutPreview = signal<CheckoutAmountQuote | null>(null);
  readonly checkoutMode = signal<'MANUAL' | 'STRIPE' | null>(null);
  readonly commissionRatePct = signal<number | null>(null);

  readonly isStripeCheckout = computed(() => this.checkoutMode() === 'STRIPE');
  readonly hasPaymentPreview = computed(() => this.checkoutPreview() != null);

  readonly paymentBreakdownHeading = computed(() =>
    `Estimación (${this.minHours()} h mín.)`,
  );

  readonly paymentBreakdownDisclaimer = computed(() => {
    if (this.isStripeCheckout()) {
      return 'Orientativo para la reserva mínima. El importe final depende del hueco que elijas; aquí el alquiler se cobra completo en Stripe.';
    }
    return 'Orientativo para la reserva mínima. El desglose exacto se confirma al elegir fecha y horario en el calendario.';
  });

  readonly managementFeeLabel = computed(() => {
    const pct = this.commissionRatePct();
    return pct != null ? `Gestión online (${pct}%)` : 'Gestión online';
  });

  readonly stepOneTitle = computed(() => {
    if (this.isStripeCheckout()) return 'Pago online del alquiler';
    const pct = this.commissionRatePct();
    return pct != null ? `Pago de la comisión (${pct}%)` : 'Pago de la comisión';
  });

  readonly stepOneDescription = computed(() => {
    if (this.isStripeCheckout()) {
      return 'Pagas en Stripe el alquiler completo. Tu hueco queda bloqueado en el calendario.';
    }
    return 'Pagas en Stripe nuestra tarifa de gestión online y tu hueco queda bloqueado en el calendario.';
  });

  readonly stepTwoDescription = computed(() => {
    if (this.isStripeCheckout()) {
      return 'Firmas el contrato digital. El alquiler ya está pagado; solo completas tus datos y la documentación. El plazo depende de los días que falten al evento.';
    }
    return 'Firmas el contrato digital, abonas la señal al propietario (Bizum o transferencia) y subes el justificante. El plazo depende de los días que falten al evento.';
  });

  readonly managementFeeAmount = computed(() => {
    const q = this.checkoutPreview();
    if (!q || this.isStripeCheckout()) return 0;
    return Math.round(q.applicationFeeCents) / 100;
  });

  readonly ownerDirectAmount = computed(() => {
    const q = this.checkoutPreview();
    return q ? Math.round(q.pendingBalanceCents) / 100 : 0;
  });

  readonly stripePayNowAmount = computed(() => {
    const q = this.checkoutPreview();
    return q ? Math.round(q.payNowCents) / 100 : 0;
  });

  readonly locationLabel = computed(() => {
    const s = this.space();
    if (!s) return 'Vicálvaro, Madrid';
    const district = s.district?.trim();
    const province = s.province?.trim();
    if (district && province) return `${district}, ${province}`;
    if (district) return district;
    if (province) return province;
    const addr = s.address?.trim();
    if (addr) {
      const first = addr.split(',')[0]?.trim();
      if (first) return first;
      return addr;
    }
    return 'Vicálvaro, Madrid';
  });

  readonly formatPrice = formatEuro;

  /**
   * Best human-facing address line for the Localización card. Picks the
   * first non-junk value: formattedAddress (Places-resolved) → address
   * → empty. Legacy spaces have `address: "undefined"` (the literal
   * string — from a `String(undefined)` bug in the wizard) so we skip
   * that value explicitly.
   */
  readonly displayAddress = computed<string>(() => {
    const s = this.space();
    if (!s) return '';
    const candidates = [s.formattedAddress, s.address];
    for (const v of candidates) {
      const trimmed = (v ?? '').trim();
      if (trimmed && trimmed.toLowerCase() !== 'undefined') return trimmed;
    }
    return '';
  });

  /**
   * Best query string for Google Maps — prefers exact lat/lon when available,
   * falls back to the human address. Empty when neither is set (hides the map).
   */
  private readonly mapQuery = computed<string>(() => {
    const s = this.space();
    if (!s) return '';
    const lat = s.latitude;
    const lon = s.longitude;
    if (typeof lat === 'number' && typeof lon === 'number') {
      return `${lat},${lon}`;
    }
    return this.displayAddress();
  });

  /** True only when we have a query AND a Google Maps API key configured. */
  readonly hasMap = computed(() => {
    const key = (environment.googleMapsApiKey ?? '').trim();
    return key.length > 0 && !key.includes('REPLACE_WITH') && this.mapQuery().length > 0;
  });

  /**
   * Sanitized iframe src for the Maps Embed API. Angular blocks unsafe
   * resource URLs in iframes by default, so we bypass with the sanitizer
   * after building the URL ourselves from controlled inputs.
   */
  readonly mapEmbedUrl = computed<SafeResourceUrl | null>(() => {
    if (!this.hasMap()) return null;
    const key = encodeURIComponent(environment.googleMapsApiKey);
    const q   = encodeURIComponent(this.mapQuery());
    const url = `https://www.google.com/maps/embed/v1/place?key=${key}&q=${q}&zoom=15`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  /** Opens Google Maps in a new tab (works without an API key). */
  readonly mapSearchUrl = computed<string>(() => {
    const q = this.mapQuery();
    if (!q) return '';
    return `https://www.google.com/maps?q=${encodeURIComponent(q)}`;
  });

  /**
   * Features of the current space, grouped by category. Categories and icons
   * come from Firestore (/categories, /features); the space only stores the
   * featureIds it has selected.
   */
  readonly showFeaturesSection = computed(
    () => (this.space()?.featureIds?.length ?? 0) > 0,
  );

  readonly featuresSectionLoading = computed(
    () => this.showFeaturesSection() && !this.featuresLoaded(),
  );

  readonly activeFeaturesGrouped = computed<GroupedFeatures[]>(() => {
    if (!this.featuresLoaded()) return [];
    return this.featuresAgent.groupedByFeatureIds(this.space()?.featureIds ?? []);
  });

  readonly activeFeatureCount = computed(() =>
    this.activeFeaturesGrouped().reduce((n, g) => n + g.features.length, 0),
  );

  constructor() {
    effect(() => {
      const images = this.galleryImages();
      const current = this.activeImageIndex();
      if (images.length === 0) {
        if (current !== 0) this.activeImageIndex.set(0);
        return;
      }
      if (current > images.length - 1) {
        this.activeImageIndex.set(0);
      }
    });

    effect(() => {
      const space = this.space();
      const total = this.totalPrice();
      const ownerId = space?.ownerId?.trim();
      if (!ownerId || total <= 0) {
        this.checkoutPreview.set(null);
        this.checkoutMode.set(null);
        this.commissionRatePct.set(null);
        return;
      }
      void this.bookingService.getOwnerCheckoutMode(ownerId, total).then((data) => {
        if (this.space()?.ownerId !== ownerId || this.totalPrice() !== total) return;
        this.checkoutMode.set(data.checkoutMode);
        this.commissionRatePct.set(data.commissionRatePct);
        this.checkoutPreview.set(data.quote ?? null);
      }).catch(() => {
        if (this.space()?.ownerId !== ownerId) return;
        this.checkoutPreview.set(null);
        this.checkoutMode.set(null);
        this.commissionRatePct.set(null);
      });
    });

    effect(() => {
      const space = this.space();
      const loading = this.loading();
      if (loading || !space) return;

      const cover = spaceCoverThumb(space);
      const path = this.router.serializeUrl(this.spaceRoutes.canonicalUrlTree(space));
      const canonicalUrl = `${this.seo.siteOrigin()}${path}`;
      this.seo.setPage({
        title: buildSpaceSeoTitle(space.name, this.locationLabel()),
        description: buildSpaceSeoDescription(space.name, space.description, this.locationLabel()),
        canonicalPath: path,
        imageUrl: cover || undefined,
        type: 'website',
      });
      this.seo.setJsonLd('space', buildSpaceJsonLd({
        space,
        canonicalUrl,
        locationLabel: this.locationLabel(),
        imageUrl: cover ? this.seo.toAbsoluteUrl(cover) : undefined,
      }));
    });
  }

  ngOnDestroy(): void {
    this.seo.resetToDefault();
  }

  ngOnInit(): void {
    if (!this.spaceRoutes.parseRoute(this.route.snapshot.paramMap)) {
      this.router.navigate(['/404']);
      return;
    }

    void this.spaceRoutes.loadSpaceFromRoute(this.route.snapshot.paramMap).then(async space => {
      this.loading.set(false);
      if (!space) {
        this.router.navigate(['/404']);
        return;
      }
      if (!space.active) {
        const user = await new Promise<typeof this.auth.currentUser>(resolve => {
          authState(this.auth).pipe(take(1)).subscribe(u => resolve(u));
        });
        if (user?.uid !== space.ownerId) {
          this.router.navigate(['/404']);
          return;
        }
      }
      this.space.set(space);
      this.spaceRoutes.redirectToCanonicalIfNeeded(space);
    }).catch(() => {
      this.loading.set(false);
      this.router.navigate(['/404']);
    });

    this.featuresAgent.load().then(() => this.featuresLoaded.set(true));
  }

  prevImage(): void {
    const total = this.galleryImages().length;
    if (total <= 1) return;
    this.activeImageIndex.update(i => (i - 1 + total) % total);
  }

  nextImage(): void {
    const total = this.galleryImages().length;
    if (total <= 1) return;
    this.activeImageIndex.update(i => (i + 1) % total);
  }

  getShareUrl(space: Space): string {
    const path = this.router.serializeUrl(this.spaceRoutes.canonicalUrlTree(space));
    return `${window.location.origin}${path}`;
  }

  /** Opens the public availability calendar — no auth required. */
  openCalendar(): void {
    if (!this.space()) return;
    this.calendarModalOpen.set(true);
  }

  openShareModal(): void {
    if (!this.space()) return;
    this.linkCopied.set(false);
    this.shareModalOpen.set(true);
  }

  closeShareModal(): void {
    this.shareModalOpen.set(false);
    this.linkCopied.set(false);
  }

  async copyShareLink(): Promise<void> {
    const space = this.space();
    if (!space) return;
    const url = this.getShareUrl(space);
    try {
      await navigator.clipboard.writeText(url);
      this.linkCopied.set(true);
      window.setTimeout(() => this.linkCopied.set(false), 2200);
    } catch {
      /* clipboard blocked — user can still select from the readonly field */
    }
  }

  shareOnWhatsApp(): void {
    const space = this.space();
    if (!space) return;
    const url = this.getShareUrl(space);
    const text = encodeURIComponent(`¡Mira ${space.name} en TimeFlexRent! ${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  }

  async nativeShare(): Promise<void> {
    const space = this.space();
    if (!space || !this.canNativeShare) return;
    const url = this.getShareUrl(space);
    try {
      await navigator.share({
        title: space.name,
        text: '¡Mira este espacio en TimeFlexRent!',
        url,
      });
      this.closeShareModal();
    } catch (err) {
      if ((err as DOMException)?.name !== 'AbortError') {
        console.warn('[SpaceDetail] navigator.share failed:', err);
      }
    }
  }

  /**
   * Auth-bridge for the booking flow:
   *  - Signed-in: jump straight to the user booking view.
   *  - Guest:     route to /login with a returnUrl pointing at the same
   *               booking view so post-login lands them in context.
   */
  onReserve(): void {
    if (!this.isMinHoursMet()) return;
    const space = this.space();
    if (!space) return;

    this.spaceRoutes.rememberPendingBookingSpace(space.id);
    const bookingTree = this.spaceRoutes.bookingUrlTree(space);
    const returnUrl = this.router.serializeUrl(bookingTree);
    const user = this.authUser();

    if (!user) {
      void this.router.navigate(['/login'], { queryParams: { returnUrl } });
      return;
    }
    if (!user.emailVerified) {
      void this.router.navigate(['/verificar-email']);
      return;
    }
    void this.router.navigateByUrl(bookingTree);
  }

}

